<script lang="ts" setup>
usePageSeo('seoAlpineTitle', 'seoAlpineDescription');
</script>

<template>
  <div class="alpine-page">
    <Hero 
      :title="$t('Alpine')" 
      :subtitle="$t('winterActivities1')"
      backgroundImage="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614481/refuge/outside/outside_3_yp0u8t.jpg"
    />

    <div class="content-container">
      <div class="intro-text">
        <p>{{ $t('winterActivities2') }}</p>
      </div>

      <div id="winter-school" tabindex="-1" class="feature-section">
        <div class="feature-image">
          <nuxt-img
              src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614478/refuge/outside/outside_21_p39mqv.jpg"
              :alt="$t('altWinterSchool')"
              format="webp" loading="lazy" />
        </div>
        <div class="feature-info">
          <span class="category-tag">{{ $t('trainingTag') }}</span>
          <h2>{{ $t('Winter School') }}</h2>
          <div class="description">
            <p>{{ $t('winterSchool1') }}</p>
            <p>{{ $t('winterSchool2') }}</p>
          </div>
        </div>
      </div>

      <div id="ice-climbing" tabindex="-1" class="feature-section reverse">
        <div class="feature-image">
          <nuxt-img 
              src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614486/refuge/activities/icecClimbing_1_mbnqzh.jpg"
              :alt="$t('altIceClimbing')"
              format="webp" loading="lazy" />
        </div>
        <div class="feature-info">
          <span class="category-tag">{{ $t('extremeTag') }}</span>
          <h2>{{ $t('Ice Climbing') }}</h2>
          <div class="description">
            <p v-for="paragraph in $t('iceClimbingToSplit').split('.').filter(p => p.trim())" :key="paragraph">
              {{ paragraph.trim() }}.
            </p>
          </div>
          <blockquote class="quote">
            <Icon name="ph:quotes-fill" class="quote-icon" />
            <p>{{ $t('iceClimbingQuote') }}</p>
            <cite>— Jerzy Kukuczka</cite>
          </blockquote>
        </div>
      </div>

      <div id="skiing" tabindex="-1" class="feature-section">
        <div class="feature-image">
          <nuxt-img 
              src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614489/refuge/activities/icecClimbing_3_cnb9oi.jpg"
              :alt="$t('altAlpineSkiing')"
              format="webp" loading="lazy" />
        </div>
        <div class="feature-info">
          <span class="category-tag">{{ $t('adventureTag') }}</span>
          <h2>{{ $t('Alpine Skiing') }}</h2>
          <div class="description">
            <p>{{ $t('alpineSkiing1') }}</p>
            <p>{{ $t('alpineSkiing2') }}</p>
            <p>{{ $t('alpineSkiing3') }}</p>
          </div>
          
          <div class="video-teaser">
            <h3>{{ $t('enjoyTheoharopoulos') }}</h3>
            <div class="video-wrapper">
              <iframe 
                src="https://www.youtube-nocookie.com/embed/yvFkXp75v24"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen>
              </iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.alpine-page
  width: 100%

.content-container
  max-width: 1100px
  margin: 0 auto
  padding: 4rem 2rem 8rem

.intro-text
  text-align: center
  max-width: 800px
  margin: 0 auto 6rem
  font-size: 1.15rem
  line-height: 1.8
  color: rgba(0, 0, 0, 0.7)

.dark-mode .intro-text
  color: rgba(255, 255, 255, 0.7)

// Feature Section
.feature-section
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 5rem
  align-items: center
  margin-bottom: 8rem

  &.reverse
    .feature-image
      order: 2
    .feature-info
      order: 1

.feature-image
  border-radius: 12px
  overflow: hidden
  box-shadow: $shadow-modern
  aspect-ratio: 4/5

  img
    width: 100%
    height: 100%
    object-fit: cover
    transition: transform 0.8s ease

  &:hover img
    transform: scale(1.05)

.feature-info
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 1.25rem

.category-tag
  color: $color-primary
  text-transform: uppercase
  font-weight: 800
  letter-spacing: 1px
  font-size: 0.8rem

.feature-info h2
  font-size: 2.5rem
  font-weight: 700
  margin: 0
  line-height: 1.1

.description
  font-size: 1.05rem
  line-height: 1.6
  color: $color-text-main
  opacity: 0.8

  p
    margin-bottom: 1rem

.dark-mode .description
  color: $color-text-light

// Quote Styling
.quote
  margin: 1.5rem 0
  padding: 1.5rem
  background-color: rgba(0, 0, 0, 0.02)
  border-radius: 12px
  position: relative
  width: 100%

  .quote-icon
    color: $color-primary
    margin-bottom: 0.5rem
    opacity: 0.4

  p
    font-size: 1.25rem
    font-weight: 600
    font-style: italic
    margin-bottom: 0.75rem
    color: $color-dark-bg

  cite
    font-weight: 700
    text-transform: uppercase
    letter-spacing: 1px
    font-size: 0.75rem
    opacity: 0.5

.dark-mode .quote
  background-color: rgba(255, 255, 255, 0.03)
  p
    color: white

// Video Teaser
.video-teaser
  margin-top: 1.5rem
  width: 100%
  
  h3
    margin-bottom: 1rem
    font-size: 1.1rem
    font-weight: 700

.video-wrapper
  position: relative
  padding-bottom: 56.25%
  height: 0
  border-radius: 8px
  overflow: hidden
  box-shadow: $shadow-modern

  iframe
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%

@media (max-width: 1024px)
  .feature-section
    grid-template-columns: 1fr
    gap: 3rem
    text-align: center
    margin-bottom: 6rem

    &.reverse .feature-image, &.reverse .feature-info
      order: unset

  .feature-image
    aspect-ratio: 16/9
    max-height: 350px

  .feature-info
    align-items: center
</style>
